/**
 * Slice 5 — `UnverifiedDatabaseIndicator`.
 *
 * Renders a small `?` icon next to a database entry that's part of the
 * smart-fallback list (the connection's configured `database` + the per-
 * connection `knownDatabases` LRU we appended to in prior sessions).
 *
 * The icon is a visual gate that says: "this entry isn't from the server's
 * authoritative list right now". Hover / focus reveals a centralised
 * tooltip (the wording is in `fallbackTooltip.ts` — never duplicated).
 *
 * The `kind` prop distinguishes:
 *   - `'config'` — the connection's configured database
 *   - `'known'` — a previously pool-opened database from `knownDatabases`
 *
 * The DB name is intentionally NOT in the tooltip — the entry already shows
 * it, and duplicating leaks PII into any screenshot / log pipeline.
 */

import { HelpCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';
import {
  getUnverifiedDatabaseTooltip,
  type FallbackEntryKind,
} from '../utils/fallbackTooltip';

type UnverifiedDatabaseIndicatorProps = {
  kind: FallbackEntryKind;
  /**
   * When the surrounding picker has tight space (e.g. a `<select>`-flavoured
   * dropdown), the caller can opt into a tinier visual treatment.
   */
  density?: 'default' | 'compact';
  className?: string;
  'data-testid'?: string;
};

export function UnverifiedDatabaseIndicator({
  kind,
  density = 'default',
  className,
  'data-testid': dataTestId,
}: UnverifiedDatabaseIndicatorProps) {
  const tooltip = getUnverifiedDatabaseTooltip(kind);
  const sizeClass = density === 'compact' ? 'h-3 w-3' : 'h-3.5 w-3.5';
  return (
    <TooltipProvider>
      <Tooltip delayDuration={150}>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'inline-flex items-center text-muted-foreground/70 cursor-help',
              className,
            )}
            data-testid={dataTestId ?? `unverified-db-indicator-${kind}`}
            data-fallback-kind={kind}
            aria-label="Presence not verified"
          >
            <HelpCircle className={sizeClass} aria-hidden />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
